import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { z } from 'zod';

import AddToCalendar from '../../components/AddToCalendar';
import Map from '../../components/Map';
import MeetingCard from '../../components/MeetingCard';
import { track } from '../../lib/analytics';
import { contentQueries, useGetContent } from '../../lib/react-query/ContentQueries';
import { meetingQueries, useGetMeetingById } from '../../lib/react-query/MeetingQueries';

const searchParamsSchema = z.object({
  id: z.string(),
  token: z.string().optional(),
});

export const Route = createFileRoute('/rendez-vous/confirmation')({
  component: ConfirmationPage,
  validateSearch: searchParamsSchema,
  loaderDeps: ({ search }) => ({ id: search.id }),
  loader: async ({ context, deps }) => {
    const { queryClient } = context;
    const { id } = deps;

    // Preload meeting and content data in parallel
    await Promise.all([
      queryClient.ensureQueryData(meetingQueries.byId(id)),
      queryClient.ensureQueryData(contentQueries.main()),
    ]);
  },
  head: () => ({
    meta: [
      {
        title: 'Confirmation de rendez-vous | Azelysse Piercing & Tattoo',
      },
      {
        name: 'robots',
        content: 'noindex, nofollow',
      },
    ],
  }),
});

function ConfirmationPage() {
  const { id, token } = Route.useSearch();
  const navigate = useNavigate();
  const tracked = useRef(false);
  const { data: content } = useGetContent();
  const { data: meeting, isFetching, isError } = useGetMeetingById(id);

  // Resolve the token: prefer URL param, fallback to meeting data
  const meetingToken = token || meeting?.access_token;

  useEffect(() => {
    if (meeting && !tracked.current) {
      track('RendezVousConfirmation');
      tracked.current = true;
    }
  }, [meeting]);

  useEffect(() => {
    if (!id || (isError && !isFetching)) {
      navigate({ to: '/' });
    }
  }, [id, isError, isFetching, navigate]);

  if (isFetching) {
    return (
      <div className="mx-auto max-w-7xl px-6 pt-36 pb-32 sm:pt-48 lg:px-8 lg:pt-32">
        <p>Chargement...</p>
      </div>
    );
  }

  if (!meeting || !content) {
    return null;
  }

  return (
    <div className="mx-auto max-w-7xl px-6 pt-36 pb-32 sm:pt-48 lg:px-8 lg:pt-32">
      <h1 className="mb-8 text-2xl font-semibold">Confirmation de votre rendez-vous</h1>

      <div className="text-md flex flex-col gap-2 text-neutral-800">
        <p>Vous allez recevoir un e-mail récapitulatif de votre rendez-vous.</p>
        <p>
          Si vous avez besoin de modifier ou d'annuler votre rendez-vous, il y a un lien à cet effet
          dans cet email. Vous pouvez aussi me contacter.
        </p>
      </div>
      <div className="mt-8">
        <AddToCalendar meeting={meeting} />
      </div>

      <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-2">
        <MeetingCard meeting={meeting} content={content} showButtons token={meetingToken} />

        <div>
          <Map />
        </div>
      </div>
    </div>
  );
}
